import { HttpStatusCode } from '@angular/common/http';
import {
  Component,
  ExistingProvider,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import {
  AlertComponent,
  ButtonComponent,
  CheckboxComponent,
  EnumHelper,
  FormGroupComponent,
  FormsUtil,
  InputComponent,
  ModalService,
  ModalSettings,
  SelectComponent,
  WizardStep
} from 'mbs-ui-kit';
import { sqlServerInstanceValidator } from 'mbs-ui-shared@constants';
import { defer, noop, Observable, of, switchMap } from 'rxjs';
import { catchError, map, startWith } from 'rxjs/operators';
import {
  DatabaseServerInstances,
  SQLServerInstanceAndAuthData,
  SQLServerInstanceAuthenticationTypeEnum,
  SQLServerInstanceStepValue,
  SQLServerInstanceStepValueForm
} from 'wizards/models/sql-server-instance-models';
import { RemoteManagementWizardsService, SQLIsSysAdminOrCredentialsModel } from 'wizards/services/remote-management-wizards.service';
import { ParamsForGetDatabases, WizardStepsService } from 'wizards/services/wizard-steps.service';

import { CommonModule } from '@angular/common';

import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { HelpLinkDirective } from 'mbs-ui-shared@help/helpLink.directive';
import { StepBase } from 'wizards/steps/StepBase.class';

/**
 * Component for "SQL server instance" step
 * Used within the SQL wizard
 * @authors Roman.sh
 */
const SQLServerInstanceStepValueAccessor: ExistingProvider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => SQLServerInstanceStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-sql-server-instance-step',
  templateUrl: './sql-server-instance-step.component.html',
  providers: [SQLServerInstanceStepValueAccessor],
  imports: [
    AlertComponent,
    ButtonComponent,
    CheckboxComponent,
    CommonModule,
    FormGroupComponent,
    I18NextPipe,
    InputComponent,
    NgbTooltipModule,
    ReactiveFormsModule,
    SelectComponent,
    HelpLinkDirective
  ]
})
export class SQLServerInstanceStepComponent extends StepBase<SQLServerInstanceStepValue> implements OnInit, OnChanges {
  ability = inject(AbilityService);
  mainService: RemoteManagementWizardsService;
  i18nPipe = inject(I18NextPipe);
  modalService = inject(ModalService);
  private stepService = inject(WizardStepsService);

  @ViewChild('changeNameModal', { static: true, read: TemplateRef }) changeNameModal: TemplateRef<HTMLElement>;
  @ViewChild('notSysAdminModal', { static: true, read: TemplateRef }) notSysAdminModal: TemplateRef<HTMLElement>;

  @Input() databaseServerInstances: DatabaseServerInstances = [];

  public readonly SQLServerAuthTypes = EnumHelper.EnumToSelectIndexesArray(SQLServerInstanceAuthenticationTypeEnum);
  public readonly elementsSelector = {
    name: {
      sqlInstanceBlock: 'sql-instance-block',
      sqlInstanceControl: 'sql-instance-control',
      authTypeControl: 'auth-type-control',
      sqlUserNameInput: 'sql-user-name-input',
      sqlPasswordInput: 'sql-password-input',
      checkIsSysAdmin: 'sql-check-is-sys-admin-checkbox'
    }
  };
  private readonly modalSettings: ModalSettings = {
    header: { title: this.i18nPipe.transform('wizards:sql:sqlNotSysAdminModalTitle', { format: 'title' }) },
    footer: {
      okButton: { show: false },
      cancelButton: { text: this.i18nPipe.transform('shared:buttons.close', { format: 'title' }) }
    }
  };

  public changeNameForm: FormGroup<{ sqlInstanceName: FormControl<string> }> = new FormGroup({
    sqlInstanceName: new FormControl('', [Validators.required, this.pathValidator.bind(this)])
  });

  public initialPass = '';
  public passwordInputType = 'password';
  public authTypeIsWindows = false;
  public canReadHelpMarketing = this.ability.can('read', 'HelpMarketing');

  private isSysAdmin = false;
  private credentialIsValid = null;
  private cashValueAfterCheck: SQLServerInstanceAndAuthData;

  private get changeNameSettings(): ModalSettings {
    return {
      header: { title: this.i18nPipe.transform('wizards:sql:editSQLServerNameLabel') },
      footer: {
        okButton: {
          text: this.i18nPipe.transform('shared:buttons.save', { format: 'title' }),
          disabled$: this.changeNameForm.valueChanges.pipe(
            startWith(true),
            switchMap(() => defer(() => of(!this.changeNameForm.valid)))
          )
        },
        cancelButton: { text: this.i18nPipe.transform('shared:buttons.cancel', { format: 'title' }) }
      }
    };
  }

  constructor() {
    const mainService = inject(RemoteManagementWizardsService);

    super();

    this.mainService = mainService;
  }

  ngOnInit(): void {
    this.initForm();
  }

  initForm(): void {
    this.stepForm = new FormGroup<SQLServerInstanceStepValueForm>({
      sqlServerInstanceName: new FormControl('', Validators.required),
      authenticationType: new FormControl(SQLServerInstanceAuthenticationTypeEnum['Windows Authentication']),
      userName: new FormControl('', [Validators.required, this.dbCredentialsValidator.bind(this, false)]),
      password: new FormControl('', [Validators.required, this.dbCredentialsValidator.bind(this, true)]),
      checkIsSysAdmin: new FormControl(true, this.checkSysAdminValidator.bind(this)),
      useSecureConnection: new FormControl(false)
    });

    this.initFormEvents();
  }

  protected initFormEvents(): void {
    super.initFormEvents();

    this.stepForm
      .get('authenticationType')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe({ next: (value) => this.authenticationTypeChangeHandler(value) });
  }

  onStepFormChange(value: SQLServerInstanceStepValue): void {
    this.value = { ...value, valid: this.databaseServerInstances?.length && this.stepForm.valid && this.credentialIsValid };
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.stepForm && changes.databaseServerInstances?.currentValue?.length && !this.value?.sqlServerInstanceName) {
      this.stepForm.get('sqlServerInstanceName').setValue(changes.databaseServerInstances?.currentValue[0].Name);
    }
  }

  updateForm(value: SQLServerInstanceStepValue): void {
    if (this.databaseServerInstances?.length && !value.sqlServerInstanceName) {
      value.sqlServerInstanceName = this.databaseServerInstances[0].Name;
    }

    this.stepForm.reset(value);

    if (this.isEdit) {
      this.initialPass = value.password;

      if (this.mainService.Plan$.value) this.loadDatabases();
    }
  }

  forceValid(step: WizardStep & { isSave?: boolean; isNext?: boolean } = null): void {
    if (!this.authTypeIsWindows && (!this.value.userName || !this.value.password)) {
      return void this.invalidCredentialsHandler();
    }

    if (this.databaseServerInstances?.length && !this.value.valid && (step.isNext || step.isSave)) {
      return void this.chainRequestsAfterNextOrSave(step.isSave);
    }

    FormsUtil.triggerValidation(this.stepForm);
    this.resetValidStateForValidFields();
    this.stepForm.get('checkIsSysAdmin').updateValueAndValidity();
  }

  authenticationTypeChangeHandler(authType: SQLServerInstanceAuthenticationTypeEnum): void {
    this.authTypeIsWindows = authType === SQLServerInstanceAuthenticationTypeEnum['Windows Authentication'];

    const action = this.authTypeIsWindows ? 'disable' : 'enable';

    ['userName', 'password'].forEach((key) => this.stepForm.get(key)[action]());
  }

  changePasswordType(): void {
    this.passwordInputType = this.passwordInputType === 'password' ? 'text' : 'password';
  }

  resetValidStateIfChangedData(): void {
    const needCheckOrSQLAuthType = this.value.checkIsSysAdmin || !this.authTypeIsWindows;
    const notCashOrChangedValue =
      !this.cashValueAfterCheck ||
      Object.keys(this.cashValueAfterCheck).some(
        (key: string) => (key !== 'checkIsSysAdmin' || needCheckOrSQLAuthType) && this.cashValueAfterCheck[key] !== this.value[key]
      );

    if (notCashOrChangedValue) {
      this.resetValidFlags();
      this.resetValidStateForValidFields();
    }
  }

  editSQLServerNameClickHandler(): void {
    this.changeNameForm.get('sqlInstanceName').setValue(this.value.sqlServerInstanceName);
    this.modalService
      .open(this.changeNameSettings, this.changeNameModal)
      .then((confirm) => {
        if (!confirm) return;

        const newValue = this.changeNameForm.get('sqlInstanceName').value;

        if (newValue === this.value.sqlServerInstanceName) return;

        this.stepForm.get('sqlServerInstanceName').setValue(newValue);
      })
      .catch(noop);
  }

  private pathValidator(control: AbstractControl): ValidationErrors | null {
    return sqlServerInstanceValidator.test(control.value)
      ? { invalidPath: { message: this.i18nPipe.transform('wizards:errorPathForDB') } }
      : null;
  }

  private checkSysAdminValidator(control: AbstractControl): ValidationErrors | null {
    return !control.value || this.isSysAdmin ? null : { isNotSysAdmin: true };
  }

  private resetValidFlags(): void {
    this.isSysAdmin = false;
    this.credentialIsValid = null;
  }

  private getParamsForRequests(): SQLIsSysAdminOrCredentialsModel {
    const password = this.value.password || '';

    return {
      hid: this.mainService.hid,
      profile: this.mainService.profile,
      isWindowsAuth: this.authTypeIsWindows,
      instanceName: this.value.sqlServerInstanceName,
      userName: this.value.userName || '',
      password,
      planId: this.isCreate ? null : this.mainService.planId
    };
  }

  private loadDatabases(): void {
    this.loadInfo.emit({ loading: true });

    this.getDatabasesRequest({ PayLoad: true }, this.getParamsForRequests())
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => this.loadInfo.emit({ loading: false }),
        error: () => this.loadInfo.emit({ loading: false })
      });
  }

  private chainRequestsAfterNextOrSave(isSave: boolean): void {
    this.loadInfo.emit({ loading: true });

    const isNeedCheck = this.value.checkIsSysAdmin && !this.isSysAdmin;
    const params = this.getParamsForRequests();

    this.resetValidFlags();

    this.mainService
      .checkSQLCredentials(params)
      .pipe(
        untilDestroyed(this),
        switchMap((result) => {
          const isOk = result?.PayLoad && result?.StatusCode === HttpStatusCode.Ok;

          if (isOk) {
            return isNeedCheck ? this.mainService.checkSQLInstanceIsSysadminRoleMember(params) : of(result);
          }

          return of(null);
        }),
        switchMap((result) => this.getDatabasesRequest(result, params))
      )
      .subscribe({
        next: (result) => (result ? this.updateAfterLoadData(!!result.PayLoad, isSave) : this.invalidCredentialsHandler()),
        error: () => this.loadInfo.emit({ loading: false })
      });
  }

  private getDatabasesRequest(
    result: { PayLoad: boolean; StatusCode?: number },
    params: SQLIsSysAdminOrCredentialsModel
  ): Observable<{ PayLoad: boolean; StatusCode?: number }> {
    if (!result?.PayLoad) return of(result);

    const newParams: ParamsForGetDatabases = {
      agentType: 'backup',
      commandType: 'GetDatabases',
      params: {
        instanceName: params.instanceName,
        isWindowsAuth: params.isWindowsAuth,
        userName: params.userName,
        password: params.password,
        planId: this.mainService.planId || ''
      }
    };

    return this.stepService.getRemoteCommandData(newParams, this.mainService.targetComputerData?.hid || this.mainService.hid).pipe(
      catchError(() => of({ PayLoad: false, StatusCode: HttpStatusCode.ServiceUnavailable })),
      map((result) => {
        this.mainService.dataBases$.next(result?.data?.items || []);
        return { PayLoad: !!result?.data?.items };
      }),
      untilDestroyed(this)
    );
  }

  private updateAfterLoadData(payload: boolean, isSave: boolean): void {
    this.isSysAdmin = payload;
    this.credentialIsValid = true;

    FormsUtil.triggerValidation(this.stepForm);
    this.loadInfo.emit({ loading: false });

    this.setCashValueAfterCheck();

    if (!this.isSysAdmin) {
      return void this.showNotSysAdminModal();
    }

    this.nextStep.emit(isSave);
  }

  private dbCredentialsValidator(isPassword: boolean): ValidationErrors | null {
    return this.credentialIsValid === false
      ? {
          invalidCredentials: isPassword ? { message: this.i18nPipe.transform('wizards:sql:sqlInvalidCredentialsError') } : true
        }
      : null;
  }

  private setCashValueAfterCheck(): void {
    this.cashValueAfterCheck = {
      sqlServerInstanceName: this.value.sqlServerInstanceName,
      authenticationType: this.value.authenticationType,
      userName: this.value.userName,
      password: this.value.password,
      checkIsSysAdmin: this.value.checkIsSysAdmin
    };
  }

  private invalidCredentialsHandler(): void {
    this.credentialIsValid = false;
    FormsUtil.triggerValidation(this.stepForm);

    this.loadInfo.emit({ loading: false });
  }

  private showNotSysAdminModal(): void {
    this.modalService.open(this.modalSettings, this.notSysAdminModal).then(noop).catch(noop);
  }

  passwordFocusHandler(event): void {
    if (event?.target?.value && this.initialPass && event.target.value === this.initialPass) {
      this.stepForm.get('password').setValue('');
    }
  }

  passwordBlurHandler(event): void {
    if (event?.target && !event.target.value && this.initialPass) {
      this.stepForm.get('password').setValue(this.initialPass);
    }
  }
}
